import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useTimeout } from 'usehooks-ts';

import { useCurrentUser } from '~/client/hooks/storeHooks';
import { useDetectPageHidden } from '~/client/hooks/useDetectPageHidden';
import { logout } from '~/client/lib/auth';

const getLogoutDelay = (sessionExpires: string) => {
  const start = DateTime.now();
  const end = DateTime.fromISO(sessionExpires);
  const hasExpired = end < start;

  return hasExpired ? 1 : end.diff(start).milliseconds + 5000; // Add additional 5s buffer
};

/**
 * Ensures the user is logged out once their session has expired.
 * Particularly if they are offline and no network requests are being made
 */
export const useEnsureHasActiveSession = () => {
  const pageHidden = useDetectPageHidden();

  const { sessionExpires } = useCurrentUser() || {};

  const logoutDelayMs = useMemo(
    () => (sessionExpires && !pageHidden ? getLogoutDelay(sessionExpires) : null),
    // Recalculate the delay after refocusing the app as the browser will
    // unload timeouts after a long period of inactivity
    [sessionExpires, pageHidden],
  );

  useTimeout(logout, logoutDelayMs);
};
